import React, { forwardRef } from "react";
import { Icon, type IconName } from "./icons";

export function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand-mark ${compact ? "brand-mark--compact" : ""}`} aria-label="Nelurio">
      {/* Lightweight echo of the 3D app icon (book + N + star, transparent) —
          pure paths/gradients, no filters, so it stays cheap at every size. */}
      <svg className="brand-mark__icon" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="nlbm-page-l" x1="10" y1="12" x2="24" y2="42" gradientUnits="userSpaceOnUse">
            <stop stopColor="#f4efff" /><stop offset="1" stopColor="#cfc4f2" />
          </linearGradient>
          <linearGradient id="nlbm-page-r" x1="38" y1="12" x2="24" y2="42" gradientUnits="userSpaceOnUse">
            <stop stopColor="#ffffff" /><stop offset="1" stopColor="#e6def8" />
          </linearGradient>
          <linearGradient id="nlbm-edge" x1="0" y1="36" x2="0" y2="46" gradientUnits="userSpaceOnUse">
            <stop stopColor="#3b2a86" /><stop offset="1" stopColor="#211a60" />
          </linearGradient>
          <linearGradient id="nlbm-n" x1="24" y1="18" x2="24" y2="24" gradientUnits="userSpaceOnUse">
            <stop stopColor="#7a5cff" /><stop offset="1" stopColor="#4b2fd8" />
          </linearGradient>
          <radialGradient id="nlbm-star" cx=".38" cy=".3" r=".9">
            <stop stopColor="#fff3cf" /><stop offset=".55" stopColor="#ffd36a" /><stop offset="1" stopColor="#e8932c" />
          </radialGradient>
        </defs>
        <path d="M3.5 39.4C11.6 36.3 18.6 37.8 24 42.6C29.4 37.8 36.4 36.3 44.5 39.4L44.5 42.7C36.4 39.6 29.4 41.1 24 45.9C18.6 41.1 11.6 39.6 3.5 42.7Z" fill="url(#nlbm-edge)" />
        <path d="M3.5 15c8.2-2.8 15.2-1 20.5 3.9v23.7c-5.4-4.8-12.4-6.3-20.5-3.2Z" fill="url(#nlbm-page-l)" />
        <path d="M44.5 15c-8.2-2.8-15.2-1-20.5 3.9v23.7c5.4-4.8 12.4-6.3 20.5-3.2Z" fill="url(#nlbm-page-r)" />
        <path d="M16.4 23.1 24 18.9l7.6 4.2" fill="none" stroke="url(#nlbm-n)" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="24" cy="18.9" r="2.3" fill="url(#nlbm-star)" />
        <path d="M24 1.4 25.6 7.7 30.4 9.8 25.6 11.4 24 15.8 22.4 11.4 17.6 9.8 22.4 7.7Z" fill="url(#nlbm-star)" />
      </svg>
      {!compact && <span className="brand-mark__word">Nelurio</span>}
    </span>
  );
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  icon?: IconName;
  loading?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", icon, loading, className = "", children, disabled, ...props },
  ref
) {
  return (
    <button ref={ref} className={`nl-button nl-button--${variant} ${className}`} disabled={disabled || loading} {...props}>
      {loading ? <span className="nl-spinner" /> : icon ? <Icon name={icon} size={18} /> : null}
      <span>{children}</span>
    </button>
  );
});

export function IconButton({ label, icon, className = "", ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: IconName }) {
  return (
    <button className={`nl-icon-button ${className}`} aria-label={label} title={label} {...props}>
      <Icon name={icon} />
    </button>
  );
}

export function Card({ className = "", children, elevated = false }: { className?: string; children: React.ReactNode; elevated?: boolean }) {
  return <section className={`nl-card ${elevated ? "nl-card--elevated" : ""} ${className}`}>{children}</section>;
}

export function Badge({ tone = "neutral", children, icon }: { tone?: "purple" | "blue" | "coral" | "amber" | "green" | "neutral"; children: React.ReactNode; icon?: IconName }) {
  return <span className={`nl-badge nl-badge--${tone}`}>{icon && <Icon name={icon} size={13} />}{children}</span>;
}

export const Field = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }>(
  function Field({ label, hint, error, className = "", ...props }, ref) {
    return (
      <label className={`nl-field ${className}`}>
        <span className="nl-field__label">{label}</span>
        <input ref={ref} className={`nl-input ${error ? "nl-input--error" : ""}`} aria-invalid={error ? true : undefined} {...props} />
        {(error || hint) && <span className={`nl-field__hint ${error ? "nl-field__hint--error" : ""}`}>{error || hint}</span>}
      </label>
    );
  }
);

export function SelectField({ label, children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: React.ReactNode }) {
  return (
    <label className="nl-field">
      <span className="nl-field__label">{label}</span>
      <select className="nl-input nl-select" {...props}>{children}</select>
    </label>
  );
}

export function TextAreaField({ label, hint, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string }) {
  return (
    <label className="nl-field">
      <span className="nl-field__label">{label}</span>
      <textarea className="nl-input nl-textarea" {...props} />
      {hint && <span className="nl-field__hint">{hint}</span>}
    </label>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <header className="nl-page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="nl-page-header__actions">{actions}</div>}
    </header>
  );
}

export function EmptyState({ icon = "sparkle", title, description, action }: { icon?: IconName; title: string; description?: string; action?: React.ReactNode }) {
  return (
    <div className="nl-empty">
      <span className="nl-empty__icon"><Icon name={icon} size={26} /></span>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}

export function StatCard({ label, value, icon, tone = "purple", note }: { label: string; value: React.ReactNode; icon: IconName; tone?: "purple" | "blue" | "coral" | "amber" | "green"; note?: string }) {
  return (
    <Card className={`nl-stat nl-stat--${tone}`}>
      <span className="nl-stat__icon"><Icon name={icon} /></span>
      <span className="nl-stat__label">{label}</span>
      <strong>{value}</strong>
      {note && <small>{note}</small>}
    </Card>
  );
}

export function Skeleton({ width = "100%", height = 16 }: { width?: string | number; height?: number }) {
  return <span className="nl-skeleton" style={{ width, height }} aria-hidden="true" />;
}
